/**
 * iso/tile.ts — Tile System
 *
 * The floor beneath every room in Hearth. Hardwood planks that creak warmly,
 * stone flags that whisper of old castles, carpets that invite you to sit
 * cross-legged with friends. Every tile is a piece of a home.
 *
 * This module owns the grid, knows how tiles relate to each other, and draws
 * each isometric diamond. It knows nothing about characters or furniture —
 * those live in room/ and character/. The tile is the canvas; others paint on it.
 */

import { worldToScreen, applyCamera, TILE_W, TILE_H, Z_SCALE, type Camera } from "./projection.js";

// ─── Tile Types ───────────────────────────────────────────────────────────────

/**
 * Every material a room floor or wall can be made of.
 * These are moods as much as they are materials.
 */
export type TileType =
  | "floor"       // Default smooth plank — a blank canvas for a life
  | "wall"        // Opaque surface — boundary of the world
  | "void"        // Empty space — unfilled, not walkable, dark
  | "water"       // Still or gently rippling — calming, reflective
  | "grass"       // Soft underfoot — outdoor courtyards, garden rooms
  | "wood"        // Warm hardwood — living rooms, libraries, studios
  | "stone"       // Cool flagstone — workshops, dungeons, old places
  | "carpet"      // Soft pile — bedrooms, reading nooks, snug corners
  | "marble"      // Polished white — grand halls, markets, terraces
  | "dirt"        // Packed earth — wilderness paths, cave floors
  | "cobblestone" // Rounded stones — town squares, old streets
  | "snow"        // Fresh powder — mountain peaks, winter zones
  | "lava"        // Molten rock — danger zones, volcanic vents
  | "crystal"     // Luminescent mineral — underground caverns
  | "sand"        // Fine grain — beaches, desert approaches
  | "stone_dark"  // Obsidian slate — deep underground, forges
  | "dock";       // Weathered planks — harbors, piers, boardwalks

/** Which tiles characters can walk on */
const WALKABLE_TYPES = new Set<TileType>([
  "floor", "water", "grass", "wood", "stone", "carpet",
  "marble", "dirt", "cobblestone", "snow", "crystal", "sand", "stone_dark", "dock",
]);

// ─── Color Palette ────────────────────────────────────────────────────────────

/**
 * The color palette for each tile type. Muted, warm, and cozy — these aren't
 * corporate grays. They're the colors of lived-in spaces, worn surfaces,
 * lamplight on old wood.
 *
 * Each type has a base color, a light edge (facing up/northwest), and a dark
 * edge (facing down/southeast) for that essential isometric shading.
 */
const TILE_PALETTE: Record<TileType, { base: string; light: string; dark: string; top: string }> = {
  floor:       { base: "#2a2a3e", light: "#3a3a52", dark: "#1e1e2e", top: "#32324a" },
  wall:        { base: "#3a3040", light: "#504060", dark: "#28202e", top: "#3a3040" },
  void:        { base: "#0d0d14", light: "#141420", dark: "#080810", top: "#0d0d14" },
  water:       { base: "#1a2a3e", light: "#22365a", dark: "#121e2e", top: "#1e3048" },
  grass:       { base: "#1e3018", light: "#28401e", dark: "#162410", top: "#223820" },
  wood:        { base: "#3d3020", light: "#52402a", dark: "#2a2016", top: "#453828" },
  stone:       { base: "#2e2e2e", light: "#3e3e3e", dark: "#1e1e1e", top: "#363636" },
  carpet:      { base: "#2e1e2e", light: "#3e2840", dark: "#20141e", top: "#362030" },
  marble:      { base: "#3a3848", light: "#4e4c5e", dark: "#2a2838", top: "#444258" },
  dirt:        { base: "#2e2218", light: "#3c2e20", dark: "#201810", top: "#342820" },
  cobblestone: { base: "#38342e", light: "#4a4640", dark: "#28241e", top: "#403c38" },
  snow:        { base: "#4a4e58", light: "#5e6270", dark: "#3a3e48", top: "#545862" },
  lava:        { base: "#3a1808", light: "#582010", dark: "#2a1004", top: "#4a1c0c" },
  crystal:     { base: "#2a1e3e", light: "#3e2e58", dark: "#1e1428", top: "#342648" },
  sand:        { base: "#3e3420", light: "#50442a", dark: "#2c2416", top: "#463c28" },
  stone_dark:  { base: "#1e1e22", light: "#2a2a30", dark: "#141418", top: "#242428" },
  dock:        { base: "#3a3028", light: "#4e4038", dark: "#2a201c", top: "#423830" },
};

// ─── Variant System ───────────────────────────────────────────────────────────

/**
 * Tile variant — computed from neighboring tiles.
 *
 * Variants let edge tiles look different from center tiles: a grass tile
 * at the edge of a stone courtyard can have a slightly different color or
 * eventually a different sprite. For now, variant is an integer 0–15
 * encoding which of the four cardinal neighbors are the same type (bitmask:
 * N=1, E=2, S=4, W=8). Center tiles (all neighbors match) = 15.
 */
export type TileVariant = number; // 0–15

// ─── Tile Data ────────────────────────────────────────────────────────────────

export interface TileData {
  type: TileType;
  /** Elevation in world Z units — 0 is ground, positive is raised */
  elevation: number;
  /**
   * Neighbor bitmask: N=1, E=2, S=4, W=8
   * 15 = surrounded on all sides (interior tile)
   * 0  = isolated
   */
  variant: TileVariant;
}

// ─── Wall Material Types ──────────────────────────────────────────────────────

/**
 * Every wall material tells a story. Plaster is the warm default of a home
 * that's been lived in for years. Wood paneling is a library, a study, a
 * cabin in the mountains. Brick is industrial lofts and converted workshops.
 * Stone is old houses, basements, places with history in the mortar.
 */
export type WallMaterial = "plaster" | "wood" | "brick" | "stone" | "glass" | "stone_dark" | "crystal" | "ornate";

/**
 * Wall material palettes — each material has color zones for:
 *   upper:       Main wall surface color (above chair rail)
 *   lower:       Wainscoting / lower panel (below chair rail)
 *   molding:     Crown molding and chair rail
 *   baseboard:   Floor-level trim
 *   frame:       Window and door frame
 *   glass:       Window glass fill
 *   glassHighlight: Window glass highlight (sky reflection)
 *   accent:      Subtle texture accent lines
 */
interface WallPalette {
  upper: string;
  upperShade: string;
  lower: string;
  lowerShade: string;
  molding: string;
  moldingHighlight: string;
  baseboard: string;
  frame: string;
  frameHighlight: string;
  glass: string;
  glassHighlight: string;
  accent: string;
}

const WALL_PALETTES: Record<WallMaterial, WallPalette> = {
  plaster: {
    upper:            "#3d3458",
    upperShade:       "#302848",
    lower:            "#352e50",
    lowerShade:       "#2a2440",
    molding:          "#584a70",
    moldingHighlight: "#6a5c82",
    baseboard:        "#453a5e",
    frame:            "#504268",
    frameHighlight:   "#625478",
    glass:            "#0d1a2e",
    glassHighlight:   "#1a2e48",
    accent:           "#2e2644",
  },
  wood: {
    upper:            "#3e2e20",
    upperShade:       "#30221a",
    lower:            "#362818",
    lowerShade:       "#2a1e12",
    molding:          "#5a4430",
    moldingHighlight: "#6e5840",
    baseboard:        "#4a3628",
    frame:            "#5a4430",
    frameHighlight:   "#6e5840",
    glass:            "#0d1a2e",
    glassHighlight:   "#1a2e48",
    accent:           "#2a1e14",
  },
  brick: {
    upper:            "#3a2828",
    upperShade:       "#2e2020",
    lower:            "#342222",
    lowerShade:       "#281a1a",
    molding:          "#4a3838",
    moldingHighlight: "#5a4848",
    baseboard:        "#3a2a2a",
    frame:            "#4a3838",
    frameHighlight:   "#5a4848",
    glass:            "#0d1a2e",
    glassHighlight:   "#1a2e48",
    accent:           "#221818",
  },
  stone: {
    upper:            "#323232",
    upperShade:       "#282828",
    lower:            "#2c2c2c",
    lowerShade:       "#222222",
    molding:          "#424242",
    moldingHighlight: "#505050",
    baseboard:        "#383838",
    frame:            "#424242",
    frameHighlight:   "#505050",
    glass:            "#0d1a2e",
    glassHighlight:   "#1a2e48",
    accent:           "#1e1e1e",
  },
  glass: {
    upper:            "#1a2a40",
    upperShade:       "#142030",
    lower:            "#162438",
    lowerShade:       "#101c2e",
    molding:          "#2a3a50",
    moldingHighlight: "#3a4a60",
    baseboard:        "#223248",
    frame:            "#2a3a50",
    frameHighlight:   "#3a4a60",
    glass:            "#0a1828",
    glassHighlight:   "#162840",
    accent:           "#101e30",
  },
  stone_dark: {
    upper:            "#1a1a20",
    upperShade:       "#141418",
    lower:            "#161618",
    lowerShade:       "#101012",
    molding:          "#28282e",
    moldingHighlight: "#343438",
    baseboard:        "#1e1e22",
    frame:            "#28282e",
    frameHighlight:   "#343438",
    glass:            "#080c18",
    glassHighlight:   "#101828",
    accent:           "#0e0e12",
  },
  crystal: {
    upper:            "#2a1e40",
    upperShade:       "#201838",
    lower:            "#241a38",
    lowerShade:       "#1c1430",
    molding:          "#3e2e58",
    moldingHighlight: "#4e3e68",
    baseboard:        "#302248",
    frame:            "#3e2e58",
    frameHighlight:   "#4e3e68",
    glass:            "#0a0818",
    glassHighlight:   "#1a1430",
    accent:           "#181028",
  },
  ornate: {
    upper:            "#3a2838",
    upperShade:       "#2e2030",
    lower:            "#342430",
    lowerShade:       "#281c28",
    molding:          "#5a3c50",
    moldingHighlight: "#6e5068",
    baseboard:        "#4a3040",
    frame:            "#5a3c50",
    frameHighlight:   "#6e5068",
    glass:            "#0d1220",
    glassHighlight:   "#1a2438",
    accent:           "#221820",
  },
};

// ─── Wall Segments ────────────────────────────────────────────────────────────

/**
 * A wall is a vertical surface on one edge of a tile.
 *
 * Walls in isometric rooms are rendered as tall parallelograms hanging off
 * the north or west edges of floor tiles. The "n" and "w" sides face the
 * camera; "s" and "e" are behind walls and rarely visible.
 *
 * Each wall now carries material, window, and door information so the
 * renderer can draw architectural details — wainscoting, molding, frames.
 */
export interface WallSegment {
  /** Tile grid X where this wall starts */
  x: number;
  /** Tile grid Y where this wall starts */
  y: number;
  /** Which edge the wall sits on */
  side: "n" | "e" | "s" | "w";
  /** Wall height in world units */
  height: number;
  /** If true, render a window opening in the middle of the wall */
  hasWindow: boolean;
  /** If true, render a door opening at the base of the wall */
  hasDoor?: boolean;
  /** Wall surface material — determines color palette and texture */
  material?: WallMaterial;
}

// ─── TileGrid ─────────────────────────────────────────────────────────────────

/**
 * The TileGrid is the ground truth of a room's floor plan.
 *
 * It stores every tile's type and elevation, and auto-computes the neighbor
 * variant when tiles are set. It knows which tiles are walkable. It can give
 * you the neighbors of any tile for pathfinding, variant logic, and mood
 * calculation.
 *
 * It does not know what room it's in, who's standing on it, or what furniture
 * covers it. That's room/'s job.
 */
export class TileGrid {
  readonly width: number;
  readonly height: number;

  private readonly tiles: TileData[];

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;

    // Initialize every cell as void — "unfilled" rather than "floor"
    // so designers consciously place the tiles they want.
    this.tiles = Array.from({ length: width * height }, () => ({
      type: "void" as TileType,
      elevation: 0,
      variant: 0,
    }));
  }

  // ── Bounds Checking ────────────────────────────────────────────────────────

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  private index(x: number, y: number): number {
    return y * this.width + x;
  }

  // ── Read / Write ───────────────────────────────────────────────────────────

  /**
   * Set a tile's type and elevation. Automatically recomputes the variant
   * for this tile and its four cardinal neighbors, so edges always look right.
   */
  setTile(x: number, y: number, type: TileType, elevation: number = 0): void {
    if (!this.inBounds(x, y)) return;

    this.tiles[this.index(x, y)] = {
      type,
      elevation,
      variant: 0, // will be set below
    };

    // Recompute variant for this tile and all cardinal neighbors
    this.recomputeVariant(x, y);
    this.recomputeVariant(x, y - 1); // N
    this.recomputeVariant(x + 1, y); // E
    this.recomputeVariant(x, y + 1); // S
    this.recomputeVariant(x - 1, y); // W
  }

  /**
   * Get tile data at a grid position. Returns a void tile for out-of-bounds.
   */
  getTile(x: number, y: number): TileData {
    if (!this.inBounds(x, y)) {
      return { type: "void", elevation: 0, variant: 0 };
    }
    return this.tiles[this.index(x, y)];
  }

  // ── Variant Computation ────────────────────────────────────────────────────

  /**
   * Recompute the neighbor bitmask for one tile.
   * A neighbor "matches" if it shares the same type.
   * Bitmask: N=1, E=2, S=4, W=8
   */
  private recomputeVariant(x: number, y: number): void {
    if (!this.inBounds(x, y)) return;

    const tile = this.tiles[this.index(x, y)];
    const myType = tile.type;

    let variant = 0;
    if (this.getTile(x,     y - 1).type === myType) variant |= 1;  // N
    if (this.getTile(x + 1, y    ).type === myType) variant |= 2;  // E
    if (this.getTile(x,     y + 1).type === myType) variant |= 4;  // S
    if (this.getTile(x - 1, y    ).type === myType) variant |= 8;  // W

    tile.variant = variant;
  }

  // ── Walkability ────────────────────────────────────────────────────────────

  /**
   * Can a character stand on this tile?
   * Void tiles and out-of-bounds are never walkable.
   */
  isWalkable(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return false;
    return WALKABLE_TYPES.has(this.tiles[this.index(x, y)].type);
  }

  // ── Neighbors ─────────────────────────────────────────────────────────────

  /**
   * Get the four cardinal adjacent tiles.
   *
   * Returned as named directions so callers don't have to manage indices.
   * Out-of-bounds positions return void tiles — the grid has "walls" of
   * void at its edges, which feels right for a room.
   */
  getNeighbors(x: number, y: number): {
    n: TileData; e: TileData; s: TileData; w: TileData;
  } {
    return {
      n: this.getTile(x,     y - 1),
      e: this.getTile(x + 1, y    ),
      s: this.getTile(x,     y + 1),
      w: this.getTile(x - 1, y    ),
    };
  }

  /**
   * Get all eight neighbors (cardinal + diagonal).
   * Useful for pathfinding heuristics and mood calculations.
   */
  getAllNeighbors(x: number, y: number): Record<string, TileData> {
    return {
      n:  this.getTile(x,     y - 1),
      ne: this.getTile(x + 1, y - 1),
      e:  this.getTile(x + 1, y    ),
      se: this.getTile(x + 1, y + 1),
      s:  this.getTile(x,     y + 1),
      sw: this.getTile(x - 1, y + 1),
      w:  this.getTile(x - 1, y    ),
      nw: this.getTile(x - 1, y - 1),
    };
  }

  // ── Fill Helpers ───────────────────────────────────────────────────────────

  /**
   * Fill a rectangular region with one tile type. The quickest way to lay
   * down a room's floor.
   */
  fill(
    x: number, y: number,
    w: number, h: number,
    type: TileType,
    elevation: number = 0
  ): void {
    for (let gy = y; gy < y + h; gy++) {
      for (let gx = x; gx < x + w; gx++) {
        this.setTile(gx, gy, type, elevation);
      }
    }
  }

  /**
   * Fill the entire grid with one type. Quick-start for a new room.
   */
  fillAll(type: TileType, elevation: number = 0): void {
    this.fill(0, 0, this.width, this.height, type, elevation);
  }
}

// ─── Drawing Primitives ───────────────────────────────────────────────────────

/**
 * Draw one isometric tile diamond at the given screen position.
 *
 * The diamond is drawn as a filled polygon with four vertices:
 *   top:   (cx, cy)
 *   right: (cx + tileW/2, cy + tileH/2)
 *   bottom:(cx, cy + tileH)
 *   left:  (cx - tileW/2, cy + tileH/2)
 *
 * @param ctx     Canvas 2D context
 * @param screenX Left edge of the tile's bounding box (not center)
 * @param screenY Top point of the diamond
 * @param tileW   Tile width in pixels
 * @param tileH   Tile height in pixels
 * @param color   Fill color (hex string)
 */
export function drawTileDiamond(
  ctx: CanvasRenderingContext2D,
  screenX: number,
  screenY: number,
  tileW: number,
  tileH: number,
  color: string
): void {
  const halfW = tileW / 2;
  const halfH = tileH / 2;

  ctx.beginPath();
  ctx.moveTo(screenX,          screenY);           // top
  ctx.lineTo(screenX + halfW,  screenY + halfH);   // right
  ctx.lineTo(screenX,          screenY + tileH);   // bottom
  ctx.lineTo(screenX - halfW,  screenY + halfH);   // left
  ctx.closePath();

  ctx.fillStyle = color;
  ctx.fill();

  // Stroke with the same color to close sub-pixel anti-aliasing gaps
  // between adjacent tiles. Without this, fractional coordinates create
  // thin transparent seams that let the background bleed through.
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.lineJoin = "miter";
  ctx.stroke();
}

/**
 * Cheap deterministic hash for tile-position-based variation.
 * Returns a value in [0, 1) that's stable per tile so textures don't shimmer.
 */
function tileHash(tx: number, ty: number, seed: number = 0): number {
  let h = (tx * 374761393 + ty * 668265263 + seed * 1274126177) | 0;
  h = ((h ^ (h >> 13)) * 1103515245) | 0;
  return ((h >>> 0) % 10000) / 10000;
}

/**
 * Draw a shaded tile diamond with material-specific detail.
 *
 * Each material gets its own tactile texture on top of the base color:
 *   - wood:   plank grain lines running diagonally
 *   - stone:  mortar seams dividing the tile into irregular blocks
 *   - carpet: soft pile texture with slightly raised edge
 *   - grass:  subtle blade hints
 *   - water:  gentle luminous shimmer
 *
 * The goal is warmth, not photorealism. Every detail should make you want
 * to take your shoes off and walk on it.
 */
function drawTileShaded(
  ctx: CanvasRenderingContext2D,
  screenX: number,
  screenY: number,
  palette: { base: string; light: string; dark: string; top: string },
  tileW: number,
  tileH: number,
  tileType: TileType = "floor",
  tileX: number = 0,
  tileY: number = 0,
  variant: number = 15,
  timestamp: number = 0
): void {
  const halfW = tileW / 2;
  const halfH = tileH / 2;

  // Top face — base color
  drawTileDiamond(ctx, screenX, screenY, tileW, tileH, palette.top);

  // ── Diamond clipping path for all material overlays ───────────────────
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(screenX,          screenY);
  ctx.lineTo(screenX + halfW,  screenY + halfH);
  ctx.lineTo(screenX,          screenY + tileH);
  ctx.lineTo(screenX - halfW,  screenY + halfH);
  ctx.closePath();
  ctx.clip();

  // Per-tile variation seed
  const h = tileHash(tileX, tileY);

  if (tileType === "wood" || tileType === "floor") {
    // ── Wood plank grain ──────────────────────────────────────────────
    // 3-4 planks running in isometric "north-south" direction (top-left to bottom-right)
    const plankCount = 3 + (tileHash(tileX, tileY, 7) > 0.5 ? 1 : 0);
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth   = 0.6;
    ctx.globalAlpha = 0.4;

    for (let p = 1; p < plankCount; p++) {
      const f = p / plankCount;
      // Plank seams run parallel to the left diamond edge (NW→SE axis)
      const sx = screenX - halfW * (1 - f) + halfW * f;
      const sy = screenY + halfH * f;
      const ex = screenX - halfW * f + halfW * (f - 1);
      const ey = screenY + tileH - halfH * (1 - f);

      // Slight wobble to prevent mechanical look
      const wobble = (tileHash(tileX, tileY, p * 13) - 0.5) * 1.5;

      ctx.beginPath();
      ctx.moveTo(sx + wobble, sy);
      ctx.lineTo(screenX + wobble * 0.5, screenY + halfH);
      ctx.lineTo(ex + wobble, ey);
      ctx.stroke();
    }

    // Subtle grain lines within each plank
    ctx.lineWidth   = 0.3;
    ctx.globalAlpha = 0.15;
    ctx.strokeStyle = palette.light;
    const grainLines = 2 + Math.floor(h * 3);
    for (let g = 0; g < grainLines; g++) {
      const gf = (g + 0.5) / (grainLines + 1);
      const gy1 = screenY + tileH * gf;
      const gx1 = screenX - halfW * (1 - 2 * Math.abs(gf - 0.5));
      const gx2 = screenX + halfW * (1 - 2 * Math.abs(gf - 0.5));
      ctx.beginPath();
      ctx.moveTo(gx1, gy1);
      ctx.lineTo(gx2, gy1);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Occasional knot — a tiny dark circle
    if (h > 0.75) {
      const kx = screenX + (h - 0.85) * halfW * 1.4;
      const ky = screenY + halfH + (tileHash(tileX, tileY, 3) - 0.5) * halfH * 0.8;
      ctx.fillStyle   = palette.dark;
      ctx.globalAlpha = 0.3;
      ctx.beginPath();
      ctx.arc(kx, ky, 1.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

  } else if (tileType === "stone") {
    // ── Stone mortar seams ────────────────────────────────────────────
    // One or two crossing seam lines that divide the tile into blocks
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth   = 0.8;
    ctx.globalAlpha = 0.5;

    // Horizontal seam
    const hOff = (h - 0.5) * halfH * 0.4;
    ctx.beginPath();
    ctx.moveTo(screenX - halfW * 0.9, screenY + halfH + hOff);
    ctx.lineTo(screenX + halfW * 0.9, screenY + halfH + hOff);
    ctx.stroke();

    // Vertical seam (offset by tile position for variety)
    const vOff = (tileHash(tileX, tileY, 2) - 0.5) * halfW * 0.4;
    ctx.beginPath();
    ctx.moveTo(screenX + vOff, screenY + halfH * 0.3);
    ctx.lineTo(screenX + vOff, screenY + tileH - halfH * 0.3);
    ctx.stroke();

    // Subtle surface variation — lighter patches
    ctx.fillStyle   = palette.light;
    ctx.globalAlpha = 0.08;
    const px = screenX + (h - 0.5) * halfW * 0.6;
    const py = screenY + halfH + (tileHash(tileX, tileY, 5) - 0.5) * halfH * 0.6;
    ctx.beginPath();
    ctx.ellipse(px, py, halfW * 0.3, halfH * 0.25, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

  } else if (tileType === "carpet") {
    // ── Carpet — directional pile with visible nap, woven texture ──

    // Base warmth overlay — slightly lighter center for that "plush" look
    const plushGrad = ctx.createRadialGradient(
      screenX, screenY + halfH, 0,
      screenX, screenY + halfH, halfW * 0.8
    );
    plushGrad.addColorStop(0, palette.light);
    plushGrad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = plushGrad;
    ctx.globalAlpha = 0.06;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);

    // Directional pile strokes — short diagonal lines all pointing same way
    // (carpet fibers lean in one direction, creating visible nap)
    const pileDir = 0.4; // lean angle in isometric space
    ctx.strokeStyle = palette.light;
    ctx.lineWidth = 0.4;
    ctx.globalAlpha = 0.18;
    const pileRows = 5;
    const pileCols = 6;
    for (let pr = 0; pr < pileRows; pr++) {
      for (let pc = 0; pc < pileCols; pc++) {
        const pf = (pc + 0.5) / pileCols;
        const pt = (pr + 0.5) / pileRows;
        const px = screenX + (pf - 0.5) * halfW * 1.6;
        const py = screenY + halfH * 0.4 + pt * halfH * 1.2;
        // Slight position jitter for organic feel
        const jx = (tileHash(tileX, tileY, pr * 7 + pc) - 0.5) * 1.5;
        const jy = (tileHash(tileX, tileY, pr * 11 + pc + 50) - 0.5) * 1.0;
        ctx.beginPath();
        ctx.moveTo(px + jx, py + jy);
        ctx.lineTo(px + jx + pileDir * 1.5, py + jy - 1.2);
        ctx.stroke();
      }
    }

    // Cross-weave pattern — faint perpendicular lines for woven texture
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth = 0.3;
    ctx.globalAlpha = 0.08;
    for (let cw = 0; cw < 3; cw++) {
      const cwf = (cw + 1) / 4;
      const cwx1 = screenX + (cwf - 0.5) * halfW * 1.4;
      const cwy1 = screenY + halfH * 0.3;
      const cwx2 = cwx1 + 1;
      const cwy2 = screenY + tileH - halfH * 0.3;
      ctx.beginPath();
      ctx.moveTo(cwx1, cwy1);
      ctx.lineTo(cwx2, cwy2);
      ctx.stroke();
    }

    // Stipple dots for additional softness
    ctx.fillStyle = palette.light;
    ctx.globalAlpha = 0.15;
    const dots = 12;
    for (let d = 0; d < dots; d++) {
      const dx = screenX + (tileHash(tileX * 7, tileY + d, d * 3) - 0.5) * halfW * 1.4;
      const dy = screenY + halfH + (tileHash(tileX + d, tileY * 5, d * 7) - 0.5) * halfH * 1.4;
      ctx.beginPath();
      ctx.arc(dx, dy, 0.6 + tileHash(tileX, tileY, d) * 0.6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Edge definition on non-neighbor sides (carpet border)
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth   = 1.2;
    ctx.globalAlpha = 0.3;
    // N edge (if no neighbor north)
    if (!(variant & 1)) {
      ctx.beginPath();
      ctx.moveTo(screenX - halfW + 2, screenY + halfH);
      ctx.lineTo(screenX, screenY + 2);
      ctx.stroke();
    }
    // E edge
    if (!(variant & 2)) {
      ctx.beginPath();
      ctx.moveTo(screenX, screenY + 2);
      ctx.lineTo(screenX + halfW - 2, screenY + halfH);
      ctx.stroke();
    }
    // S edge
    if (!(variant & 4)) {
      ctx.beginPath();
      ctx.moveTo(screenX + halfW - 2, screenY + halfH);
      ctx.lineTo(screenX, screenY + tileH - 2);
      ctx.stroke();
    }
    // W edge
    if (!(variant & 8)) {
      ctx.beginPath();
      ctx.moveTo(screenX, screenY + tileH - 2);
      ctx.lineTo(screenX - halfW + 2, screenY + halfH);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "grass") {
    // ── Living grass — blades sway with wind, vary height and color ──
    const blades = 8;
    const t = timestamp * 0.001; // seconds
    for (let b = 0; b < blades; b++) {
      const bx = screenX + (tileHash(tileX, tileY, b * 11) - 0.5) * halfW * 1.3;
      const by = screenY + halfH + (tileHash(tileX, tileY, b * 17) - 0.5) * halfH * 0.9;
      const bh = 2.5 + tileHash(tileX, tileY, b * 23) * 3.5;
      const phase = tileHash(tileX, tileY, b * 31) * 6.28;
      // Wind sway — each blade has its own phase offset
      const wind = Math.sin(t * 1.8 + phase + tileX * 0.5 + tileY * 0.3) * 2.2;
      const gust = Math.sin(t * 0.7 + tileX * 0.2) * 0.5; // slow global gust

      // Vary blade color between light and a brighter green
      const isLight = tileHash(tileX, tileY, b * 43) > 0.5;
      ctx.strokeStyle = isLight ? "#3a5828" : palette.light;
      ctx.lineWidth = 0.5 + tileHash(tileX, tileY, b * 53) * 0.4;
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      // Blade bends with quadratic curve for natural arc
      ctx.quadraticCurveTo(
        bx + (wind + gust) * 0.6, by - bh * 0.5,
        bx + wind + gust, by - bh
      );
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "water") {
    // ── Water — layered ripples, caustic highlights, depth gradient ──

    // Depth gradient — darker at center, lighter at edges
    const depthGrad = ctx.createRadialGradient(
      screenX, screenY + halfH, halfW * 0.1,
      screenX, screenY + halfH, halfW * 0.8
    );
    depthGrad.addColorStop(0, "rgba(10,20,40,0.12)");
    depthGrad.addColorStop(1, "rgba(30,60,90,0.04)");
    ctx.fillStyle = depthGrad;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);

    // 3 concentric ripple rings — animated expansion over time
    const wt = timestamp * 0.001;
    ctx.strokeStyle = palette.light;
    ctx.lineWidth = 0.5;
    for (let r = 0; r < 3; r++) {
      const rh = tileHash(tileX, tileY, r * 19 + 100);
      const phase = rh * 6.28;
      // Ripples slowly expand and contract
      const rippleBreath = Math.sin(wt * 0.8 + phase + r * 2.1) * 0.04;
      const rx = screenX + (rh - 0.5) * halfW * 0.5 + Math.sin(wt * 0.4 + phase) * 0.5;
      const ry = screenY + halfH + (tileHash(tileX, tileY, r * 31 + 200) - 0.5) * halfH * 0.4;
      const rScale = 0.2 + r * 0.15 + rh * 0.1 + rippleBreath;
      ctx.globalAlpha = 0.06 - r * 0.015 + Math.sin(wt * 1.2 + phase) * 0.01;
      ctx.beginPath();
      ctx.ellipse(rx, ry, halfW * rScale, halfH * rScale * 0.4, 0.2 + rh * 0.3, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Caustic light patches — animated drift and twinkle
    ctx.fillStyle = "#4a7a9a";
    for (let c = 0; c < 4; c++) {
      const ch = tileHash(tileX, tileY, c * 37 + 300);
      const ch2 = tileHash(tileX, tileY, c * 43 + 400);
      const driftX = Math.sin(wt * 0.6 + ch * 6.28) * 1.5;
      const driftY = Math.cos(wt * 0.5 + ch2 * 6.28) * 0.8;
      const cx = screenX + (ch - 0.5) * halfW * 1.1 + driftX;
      const cy = screenY + halfH + (ch2 - 0.5) * halfH * 0.9 + driftY;
      const twinkle = 0.5 + Math.sin(wt * 2.0 + ch * 12.56) * 0.5; // 0-1 pulse
      ctx.globalAlpha = (0.04 + ch * 0.04) * twinkle;
      ctx.beginPath();
      ctx.ellipse(cx, cy, halfW * 0.08 + ch2 * halfW * 0.06, halfH * 0.06, ch * Math.PI + wt * 0.2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Primary shimmer highlight — drifts slowly across tile
    ctx.fillStyle = palette.light;
    const shimDrift = Math.sin(wt * 0.3 + h * 6.28) * halfW * 0.15;
    ctx.globalAlpha = 0.08 + h * 0.06 + Math.sin(wt * 1.5 + h * 3.14) * 0.03;
    const shimX = screenX + Math.sin(wt * 0.15 + tileX * 0.7 + tileY * 0.3) * halfW * 0.3 + shimDrift;
    const shimY = screenY + halfH;
    ctx.beginPath();
    ctx.ellipse(shimX, shimY, halfW * 0.5, halfH * 0.2, 0.3 + Math.sin(wt * 0.2) * 0.1, 0, Math.PI * 2);
    ctx.fill();

    // Surface tension line along edge tiles
    if (variant !== 15) {
      ctx.strokeStyle = palette.light;
      ctx.lineWidth = 0.7;
      ctx.globalAlpha = 0.1;
      if (!(variant & 2)) {
        ctx.beginPath();
        ctx.moveTo(screenX + 1, screenY + 1);
        ctx.lineTo(screenX + halfW - 1, screenY + halfH);
        ctx.stroke();
      }
      if (!(variant & 8)) {
        ctx.beginPath();
        ctx.moveTo(screenX - halfW + 1, screenY + halfH);
        ctx.lineTo(screenX - 1, screenY + tileH - 1);
        ctx.stroke();
      }
    }

    ctx.globalAlpha = 1;

  } else if (tileType === "marble") {
    // ── Polished marble — veins, soft sheen, cool elegance ────────────
    ctx.strokeStyle = palette.light;
    ctx.lineWidth = 0.4;
    ctx.globalAlpha = 0.12;
    // 2-3 meandering veins
    const veins = 2 + (h > 0.6 ? 1 : 0);
    for (let v = 0; v < veins; v++) {
      const vf = (v + 0.5) / (veins + 1);
      const startX = screenX - halfW * 0.8 + halfW * vf * 1.6;
      const startY = screenY + halfH * 0.3;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      const cpx = startX + (tileHash(tileX, tileY, v * 7) - 0.5) * halfW * 0.6;
      const cpy = screenY + halfH;
      const endX = startX + (tileHash(tileX, tileY, v * 13) - 0.5) * halfW * 0.4;
      const endY = screenY + tileH - halfH * 0.3;
      ctx.quadraticCurveTo(cpx, cpy, endX, endY);
      ctx.stroke();
    }
    // Central sheen highlight
    const sheenGrad = ctx.createRadialGradient(screenX, screenY + halfH, 0, screenX, screenY + halfH, halfW * 0.6);
    sheenGrad.addColorStop(0, palette.light);
    sheenGrad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = sheenGrad;
    ctx.globalAlpha = 0.06;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);
    ctx.globalAlpha = 1;

  } else if (tileType === "dirt") {
    // ── Packed earth — scattered pebbles, cracks, organic roughness ──
    ctx.fillStyle = palette.light;
    ctx.globalAlpha = 0.15;
    const pebbles = 6 + Math.floor(h * 4);
    for (let p = 0; p < pebbles; p++) {
      const px = screenX + (tileHash(tileX, tileY, p * 11) - 0.5) * halfW * 1.2;
      const py = screenY + halfH + (tileHash(tileX, tileY, p * 17) - 0.5) * halfH * 0.9;
      const pr = 0.4 + tileHash(tileX, tileY, p * 23) * 0.8;
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
    }
    // Crack lines
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth = 0.4;
    ctx.globalAlpha = 0.2;
    if (h > 0.4) {
      ctx.beginPath();
      ctx.moveTo(screenX + (h - 0.5) * halfW, screenY + halfH * 0.5);
      ctx.lineTo(screenX + (h - 0.3) * halfW * 0.4, screenY + halfH * 1.3);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "cobblestone") {
    // ── Cobblestone — rounded stones with mortar gaps ─────────────────
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth = 0.7;
    ctx.globalAlpha = 0.35;
    // Irregular stone grid: 3x2 stones per tile
    for (let sr = 0; sr < 2; sr++) {
      for (let sc = 0; sc < 3; sc++) {
        const sf = (sc + 0.5) / 3;
        const st = (sr + 0.5) / 2;
        const cx = screenX + (sf - 0.5) * halfW * 1.3;
        const cy = screenY + halfH * 0.5 + st * halfH;
        const rx = halfW * 0.18 + tileHash(tileX, tileY, sr * 3 + sc) * halfW * 0.06;
        const ry = halfH * 0.14 + tileHash(tileX, tileY, sr * 5 + sc + 10) * halfH * 0.04;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "snow") {
    // ── Snow — sparkle dots, subtle blue shadows, soft drifts ─────────
    const st = timestamp * 0.001;
    ctx.fillStyle = palette.light;
    const sparkles = 8;
    for (let s = 0; s < sparkles; s++) {
      const sx = screenX + (tileHash(tileX, tileY, s * 13) - 0.5) * halfW * 1.2;
      const sy = screenY + halfH + (tileHash(tileX, tileY, s * 19) - 0.5) * halfH * 0.9;
      const twinkle = 0.5 + Math.sin(st * 2.5 + tileHash(tileX, tileY, s * 29) * 6.28) * 0.5;
      ctx.globalAlpha = 0.12 * twinkle;
      ctx.beginPath();
      ctx.arc(sx, sy, 0.5 + tileHash(tileX, tileY, s * 37) * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    // Subtle blue shadow in center
    const snowShade = ctx.createRadialGradient(screenX + halfW * 0.1, screenY + halfH * 0.8, 0, screenX, screenY + halfH, halfW * 0.5);
    snowShade.addColorStop(0, "rgba(60,70,100,0.08)");
    snowShade.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = snowShade;
    ctx.globalAlpha = 1;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);
    ctx.globalAlpha = 1;

  } else if (tileType === "lava") {
    // ── Lava — pulsing glow, dark crust, molten veins ─────────────────
    const lt = timestamp * 0.001;
    // Pulsing central glow
    const pulse = 0.5 + Math.sin(lt * 1.5 + h * 6.28) * 0.5;
    const lavaGlow = ctx.createRadialGradient(screenX, screenY + halfH, 0, screenX, screenY + halfH, halfW * 0.7);
    lavaGlow.addColorStop(0, `rgba(200,80,10,${(0.12 + pulse * 0.08).toFixed(3)})`);
    lavaGlow.addColorStop(0.6, `rgba(140,40,5,${(0.06 + pulse * 0.04).toFixed(3)})`);
    lavaGlow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = lavaGlow;
    ctx.globalAlpha = 1;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);
    // Crust cracks — bright lines
    ctx.strokeStyle = "#c05010";
    ctx.lineWidth = 0.6;
    ctx.globalAlpha = 0.15 + pulse * 0.1;
    const cracks = 2 + (h > 0.5 ? 1 : 0);
    for (let c = 0; c < cracks; c++) {
      const cx1 = screenX + (tileHash(tileX, tileY, c * 7) - 0.5) * halfW;
      const cy1 = screenY + halfH * 0.5 + tileHash(tileX, tileY, c * 11) * halfH * 0.5;
      const cx2 = cx1 + (tileHash(tileX, tileY, c * 13) - 0.5) * halfW * 0.4;
      const cy2 = cy1 + halfH * 0.4;
      ctx.beginPath();
      ctx.moveTo(cx1, cy1);
      ctx.lineTo(cx2, cy2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "crystal") {
    // ── Crystal — luminescent facets, prismatic highlights ────────────
    const ct = timestamp * 0.001;
    // Faint glow
    const crystGlow = ctx.createRadialGradient(screenX, screenY + halfH, 0, screenX, screenY + halfH, halfW * 0.6);
    const glowAlpha = 0.04 + Math.sin(ct * 0.8 + h * 6.28) * 0.02;
    crystGlow.addColorStop(0, `rgba(140,80,200,${glowAlpha.toFixed(3)})`);
    crystGlow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = crystGlow;
    ctx.globalAlpha = 1;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);
    // Facet lines
    ctx.strokeStyle = palette.light;
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.15;
    const facets = 3 + Math.floor(h * 2);
    for (let f = 0; f < facets; f++) {
      const fx = screenX + (tileHash(tileX, tileY, f * 9) - 0.5) * halfW * 0.9;
      const fy = screenY + halfH * 0.4 + tileHash(tileX, tileY, f * 15) * halfH;
      const fl = halfW * 0.15 + tileHash(tileX, tileY, f * 21) * halfW * 0.1;
      const fa = tileHash(tileX, tileY, f * 27) * Math.PI;
      ctx.beginPath();
      ctx.moveTo(fx - Math.cos(fa) * fl, fy - Math.sin(fa) * fl);
      ctx.lineTo(fx + Math.cos(fa) * fl, fy + Math.sin(fa) * fl);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "sand") {
    // ── Sand — fine grain stipple, wind ripples ──────────────────────
    ctx.fillStyle = palette.light;
    ctx.globalAlpha = 0.1;
    const grains = 14;
    for (let g = 0; g < grains; g++) {
      const gx = screenX + (tileHash(tileX, tileY, g * 11) - 0.5) * halfW * 1.3;
      const gy = screenY + halfH + (tileHash(tileX, tileY, g * 17) - 0.5) * halfH * 0.9;
      ctx.beginPath();
      ctx.arc(gx, gy, 0.3 + tileHash(tileX, tileY, g * 23) * 0.3, 0, Math.PI * 2);
      ctx.fill();
    }
    // Wind ripple lines
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth = 0.3;
    ctx.globalAlpha = 0.12;
    for (let w = 0; w < 3; w++) {
      const wf = (w + 1) / 4;
      const wy = screenY + halfH * 0.4 + wf * halfH * 1.2;
      const wobble = (tileHash(tileX, tileY, w * 31) - 0.5) * 2;
      ctx.beginPath();
      ctx.moveTo(screenX - halfW * 0.6, wy + wobble);
      ctx.quadraticCurveTo(screenX, wy - 0.5 + wobble, screenX + halfW * 0.6, wy + wobble * 0.5);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

  } else if (tileType === "stone_dark") {
    // ── Dark stone — obsidian-like, deep cracks, subtle sheen ─────────
    ctx.strokeStyle = "#282830";
    ctx.lineWidth = 0.6;
    ctx.globalAlpha = 0.4;
    // Angular crack pattern
    const cracks = 2 + (h > 0.5 ? 1 : 0);
    for (let c = 0; c < cracks; c++) {
      const cx1 = screenX + (tileHash(tileX, tileY, c * 7) - 0.5) * halfW * 0.8;
      const cy1 = screenY + halfH * 0.4 + tileHash(tileX, tileY, c * 11) * halfH * 0.4;
      const cx2 = cx1 + (tileHash(tileX, tileY, c * 13) - 0.5) * halfW * 0.3;
      const cy2 = cy1 + halfH * 0.5;
      const cx3 = cx2 + (tileHash(tileX, tileY, c * 17) - 0.5) * halfW * 0.2;
      const cy3 = cy2 + halfH * 0.3;
      ctx.beginPath();
      ctx.moveTo(cx1, cy1);
      ctx.lineTo(cx2, cy2);
      ctx.lineTo(cx3, cy3);
      ctx.stroke();
    }
    // Subtle sheen
    const darkSheen = ctx.createRadialGradient(screenX - halfW * 0.2, screenY + halfH * 0.7, 0, screenX, screenY + halfH, halfW * 0.5);
    darkSheen.addColorStop(0, "rgba(50,50,60,0.06)");
    darkSheen.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = darkSheen;
    ctx.globalAlpha = 1;
    ctx.fillRect(screenX - halfW, screenY, halfW * 2, tileH);
    ctx.globalAlpha = 1;

  } else if (tileType === "dock") {
    // ── Dock planks — wider than indoor wood, weathered, gapped ───────
    const plankCount = 3;
    ctx.strokeStyle = palette.dark;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.45;
    for (let p = 1; p < plankCount; p++) {
      const f = p / plankCount;
      const sx = screenX - halfW * (1 - f) + halfW * f;
      const sy = screenY + halfH * f;
      const ex = screenX - halfW * f + halfW * (f - 1);
      const ey = screenY + tileH - halfH * (1 - f);
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(screenX, screenY + halfH);
      ctx.lineTo(ex, ey);
      ctx.stroke();
    }
    // Weathering spots
    ctx.fillStyle = palette.dark;
    ctx.globalAlpha = 0.1;
    for (let w = 0; w < 3; w++) {
      const wx = screenX + (tileHash(tileX, tileY, w * 13) - 0.5) * halfW * 0.8;
      const wy = screenY + halfH + (tileHash(tileX, tileY, w * 19) - 0.5) * halfH * 0.5;
      ctx.beginPath();
      ctx.ellipse(wx, wy, halfW * 0.1, halfH * 0.06, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  ctx.restore();

  // Subtle grid seam — not too harsh, cozy not caged
  ctx.beginPath();
  ctx.moveTo(screenX,          screenY);
  ctx.lineTo(screenX + halfW,  screenY + halfH);
  ctx.lineTo(screenX,          screenY + tileH);
  ctx.lineTo(screenX - halfW,  screenY + halfH);
  ctx.closePath();
  ctx.strokeStyle = palette.dark;
  ctx.lineWidth = 0.5;
  ctx.stroke();
}

// ─── Wall Rendering ───────────────────────────────────────────────────────────

/**
 * Interpolate a point along the parallelogram base→top axis at parameter t.
 * t=0 is the bottom edge, t=1 is the top. Used to place horizontal bands
 * (wainscoting, moldings, window frames) at the correct isometric slant.
 */
function wallLerp(
  bx0: number, by0: number, bx1: number, by1: number,
  tx0: number, ty0: number, tx1: number, ty1: number,
  t: number
): [number, number, number, number] {
  return [
    bx0 + (tx0 - bx0) * t,
    by0 + (ty0 - by0) * t,
    bx1 + (tx1 - bx1) * t,
    by1 + (ty1 - by1) * t,
  ];
}

/**
 * Draw a filled parallelogram band between two horizontal slices of a wall.
 */
function fillWallBand(
  ctx: CanvasRenderingContext2D,
  lx0: number, ly0: number, lx1: number, ly1: number,
  ux0: number, uy0: number, ux1: number, uy1: number,
  color: string
): void {
  ctx.beginPath();
  ctx.moveTo(lx0, ly0);
  ctx.lineTo(lx1, ly1);
  ctx.lineTo(ux1, uy1);
  ctx.lineTo(ux0, uy0);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/**
 * Draw a single wall segment with full architectural detail.
 *
 * Walls are parallelograms hanging off the north or west tile edges.
 * "n" runs along the top-left edge; "w" along the top-right.
 *
 * Visual layers (bottom to top):
 *   1. Baseboard — thin dark strip at floor level
 *   2. Wainscoting — lower panel, slightly darker than upper
 *   3. Chair rail — thin horizontal molding line at ~35% height
 *   4. Upper wall — main surface (lighter, catches ambient light)
 *   5. Crown molding — highlight strip at ceiling line
 *   6. Material texture — brick courses, stone blocks, or wood grain
 *   7. Window frame + glass + mullions (if hasWindow)
 *   8. Door frame + recess (if hasDoor)
 */
export function drawWallSegment(
  ctx: CanvasRenderingContext2D,
  wall: WallSegment,
  camera: Camera,
  gameHour = 12,
  timestamp = 0,
): void {
  if (wall.side !== "n" && wall.side !== "w") return;

  const raw    = worldToScreen(wall.x, wall.y, 0, true);
  const screen = applyCamera(raw, camera);
  const zoom   = camera.zoom;
  const tileW  = TILE_W * zoom;
  const tileH  = TILE_H * zoom;
  const wallH  = wall.height * 32 * zoom;
  const halfW  = tileW / 2;
  const halfH  = tileH / 2;

  const pal = WALL_PALETTES[wall.material ?? "plaster"];
  const isNorth = wall.side === "n";

  // Bottom edge corners and top edge corners of the wall parallelogram
  let bx0: number, by0: number, bx1: number, by1: number;
  let tx0: number, ty0: number, tx1: number, ty1: number;

  if (isNorth) {
    // North wall sits on the North→East edge of the iso diamond (top-right diagonal).
    // Bottom edge follows the tile edge; top edge is the same shifted up by wallH.
    bx0 = screen.x;          by0 = screen.y;               // North corner
    bx1 = screen.x + halfW;  by1 = screen.y + halfH;       // East corner
    tx0 = bx0;               ty0 = by0 - wallH;
    tx1 = bx1;               ty1 = by1 - wallH;
  } else {
    // West wall sits on the North→West edge of the iso diamond (top-left diagonal).
    // bx0 is the North corner (top), bx1 is the West corner (bottom-left).
    bx0 = screen.x;          by0 = screen.y;               // North corner
    bx1 = screen.x - halfW;  by1 = screen.y + halfH;       // West corner
    tx0 = bx0;               ty0 = by0 - wallH;
    tx1 = bx1;               ty1 = by1 - wallH;
  }

  ctx.save();

  // ── 1. Full wall base fill ──────────────────────────────────────────────
  fillWallBand(ctx,
    bx0, by0, bx1, by1,
    tx0, ty0, tx1, ty1,
    isNorth ? pal.upper : pal.upperShade
  );

  // ── Height fractions for architectural bands ─────────────────────────────
  const BASEBOARD_T  = 0.04;
  const WAINSCOT_T   = 0.35;
  const CHAIR_RAIL_T = 0.37;
  const CROWN_BOT_T  = 0.95;

  // ── 2. Baseboard ──────────────────────────────────────────────────────────
  const [bb0x, bb0y, bb1x, bb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, BASEBOARD_T);
  fillWallBand(ctx,
    bx0, by0, bx1, by1,
    bb0x, bb0y, bb1x, bb1y,
    pal.baseboard
  );

  // ── 3. Wainscoting (lower panel) ─────────────────────────────────────────
  const [ws0x, ws0y, ws1x, ws1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, WAINSCOT_T);
  fillWallBand(ctx,
    bb0x, bb0y, bb1x, bb1y,
    ws0x, ws0y, ws1x, ws1y,
    isNorth ? pal.lower : pal.lowerShade
  );

  // Wainscoting panel inset lines — vertical dividers
  const panelCount = Math.max(2, Math.round(3 * zoom));
  ctx.strokeStyle = pal.accent;
  ctx.lineWidth   = 0.5 * zoom;
  for (let i = 1; i < panelCount; i++) {
    const f = i / panelCount;
    const pbx = bb0x + (bb1x - bb0x) * f;
    const pby = bb0y + (bb1y - bb0y) * f;
    const ptx = ws0x + (ws1x - ws0x) * f;
    const pty = ws0y + (ws1y - ws0y) * f;
    ctx.beginPath();
    ctx.moveTo(pbx, pby);
    ctx.lineTo(ptx, pty);
    ctx.stroke();
  }

  // ── 4. Chair rail ─────────────────────────────────────────────────────────
  const [cr0x, cr0y, cr1x, cr1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, CHAIR_RAIL_T);
  ctx.strokeStyle = pal.molding;
  ctx.lineWidth   = 1.5 * zoom;
  ctx.beginPath();
  ctx.moveTo(cr0x, cr0y);
  ctx.lineTo(cr1x, cr1y);
  ctx.stroke();
  // Highlight above the chair rail
  ctx.strokeStyle = pal.moldingHighlight;
  ctx.lineWidth   = 0.5 * zoom;
  ctx.beginPath();
  ctx.moveTo(cr0x, cr0y - 1 * zoom);
  ctx.lineTo(cr1x, cr1y - 1 * zoom);
  ctx.stroke();

  // ── 5. Crown molding ──────────────────────────────────────────────────────
  const [cm0x, cm0y, cm1x, cm1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, CROWN_BOT_T);
  fillWallBand(ctx,
    cm0x, cm0y, cm1x, cm1y,
    tx0, ty0, tx1, ty1,
    pal.molding
  );
  // Crown highlight line
  ctx.strokeStyle = pal.moldingHighlight;
  ctx.lineWidth   = 0.7 * zoom;
  ctx.beginPath();
  ctx.moveTo(cm0x, cm0y);
  ctx.lineTo(cm1x, cm1y);
  ctx.stroke();

  // ── 6. Material texture ─────────────────────────────────────────────────
  const mat = wall.material ?? "plaster";

  if (mat === "brick" && wallH > 0) {
    // Brick courses — horizontal mortar lines with alternating half-brick offset
    const courseH = wallH * 0.055;
    const courses = Math.floor(wallH / courseH);
    ctx.strokeStyle = pal.accent;
    ctx.lineWidth   = 0.4 * zoom;

    for (let c = 1; c < courses; c++) {
      const t = c / courses;
      if (t < BASEBOARD_T || t > CROWN_BOT_T) continue;
      const [lx, ly, rx, ry] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, t);
      ctx.beginPath();
      ctx.moveTo(lx, ly);
      ctx.lineTo(rx, ry);
      ctx.stroke();

      // Vertical mortar (alternating offset per course)
      const bricks = Math.max(2, Math.round(4 * zoom));
      const nextT = Math.min(t + 1 / courses, 1);
      const [nlx, nly, nrx, nry] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, nextT);
      const offset = (c % 2 === 0) ? 0 : 0.5 / bricks;
      for (let b = 1; b < bricks; b++) {
        const f = b / bricks + offset;
        if (f >= 1) continue;
        const mx = lx + (rx - lx) * f;
        const my = ly + (ry - ly) * f;
        const mnx = nlx + (nrx - nlx) * f;
        const mny = nly + (nry - nly) * f;
        ctx.beginPath();
        ctx.moveTo(mx, my);
        ctx.lineTo(mnx, mny);
        ctx.stroke();
      }
    }
  } else if (mat === "stone") {
    // Irregular stone block outlines — a few horizontal bands with vertical cracks
    const blockRows = 5;
    ctx.strokeStyle = pal.accent;
    ctx.lineWidth   = 0.5 * zoom;

    for (let r = 1; r < blockRows; r++) {
      const t = BASEBOARD_T + (CROWN_BOT_T - BASEBOARD_T) * (r / blockRows);
      const [lx, ly, rx, ry] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, t);
      // Slight irregularity
      const jx = zoom * 1.5;
      ctx.beginPath();
      ctx.moveTo(lx + jx * ((r * 7 % 3) - 1), ly);
      ctx.lineTo(rx - jx * ((r * 5 % 3) - 1), ry);
      ctx.stroke();
    }
  } else if (mat === "wood") {
    // Vertical wood grain lines in the upper wall
    const grainCount = Math.max(3, Math.round(5 * zoom));
    ctx.strokeStyle = pal.accent;
    ctx.lineWidth   = 0.3 * zoom;
    ctx.globalAlpha = 0.5;

    for (let g = 1; g < grainCount; g++) {
      const f = g / grainCount;
      const ux = cr0x + (cr1x - cr0x) * f;
      const uy = cr0y + (cr1y - cr0y) * f;
      const cx = cm0x + (cm1x - cm0x) * f;
      const cy = cm0y + (cm1y - cm0y) * f;
      ctx.beginPath();
      ctx.moveTo(ux, uy);
      ctx.lineTo(cx, cy);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  } else if (mat === "plaster") {
    // Plaster — subtle stucco stipple texture + faint trowel swirl marks
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = pal.moldingHighlight;
    // Hash-stable stipple dots across the wall face
    const stippleCount = Math.max(8, Math.round(16 * zoom));
    for (let s = 0; s < stippleCount; s++) {
      const sv = ((s * 374761393 + (bx0|0) * 668265263) >>> 0) % 10000 / 10000;
      const sh = ((s * 1103515245 + (by0|0) * 1274126177) >>> 0) % 10000 / 10000;
      const st = BASEBOARD_T + (CROWN_BOT_T - BASEBOARD_T) * sv;
      const sf = 0.1 + sh * 0.8;
      const [slx, sly, srx, sry] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, st);
      const sx = slx + (srx - slx) * sf;
      const sy = sly + (sry - sly) * sf;
      const sr = (0.6 + sv * 0.8) * zoom;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
    }

    // Faint trowel marks — 2–3 gentle arcs
    ctx.strokeStyle = pal.accent;
    ctx.lineWidth = 0.3 * zoom;
    ctx.globalAlpha = 0.04;
    for (let t = 0; t < 3; t++) {
      const tv = ((t * 2654435761 + (bx0|0) * 1013904223) >>> 0) % 10000 / 10000;
      const th = ((t * 1664525 + (by0|0) * 284517) >>> 0) % 10000 / 10000;
      const tBase = BASEBOARD_T + (CROWN_BOT_T - BASEBOARD_T) * (0.2 + tv * 0.6);
      const tSpan = 0.08 + th * 0.06;
      const tf = 0.2 + tv * 0.5;
      const [alx, aly, arx, ary] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, tBase);
      const [blx, bly, brx, bry] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, tBase + tSpan);
      const ax = alx + (arx - alx) * tf;
      const ay = aly + (ary - aly) * tf;
      const bxe = blx + (brx - blx) * (tf + 0.15);
      const bye = bly + (bry - bly) * (tf + 0.15);
      const cpx = (ax + bxe) / 2 + (th - 0.5) * 6 * zoom;
      const cpy = (ay + bye) / 2;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.quadraticCurveTo(cpx, cpy, bxe, bye);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // ── 7. Window ─────────────────────────────────────────────────────────────
  if (wall.hasWindow && !wall.hasDoor) {
    // Window sits in the upper portion of the wall, between chair rail and crown
    const WIN_BOT = 0.42;
    const WIN_TOP = 0.88;
    const WIN_L   = 0.18;
    const WIN_R   = 0.82;

    const [wb0x, wb0y, wb1x, wb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, WIN_BOT);
    const [wt0x, wt0y, wt1x, wt1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, WIN_TOP);

    // Interpolate left/right edges of the window within the wall width
    const wbl_x = wb0x + (wb1x - wb0x) * WIN_L;
    const wbl_y = wb0y + (wb1y - wb0y) * WIN_L;
    const wbr_x = wb0x + (wb1x - wb0x) * WIN_R;
    const wbr_y = wb0y + (wb1y - wb0y) * WIN_R;
    const wtl_x = wt0x + (wt1x - wt0x) * WIN_L;
    const wtl_y = wt0y + (wt1y - wt0y) * WIN_L;
    const wtr_x = wt0x + (wt1x - wt0x) * WIN_R;
    const wtr_y = wt0y + (wt1y - wt0y) * WIN_R;

    // Frame outer
    ctx.beginPath();
    ctx.moveTo(wbl_x, wbl_y);
    ctx.lineTo(wbr_x, wbr_y);
    ctx.lineTo(wtr_x, wtr_y);
    ctx.lineTo(wtl_x, wtl_y);
    ctx.closePath();
    ctx.fillStyle = pal.frame;
    ctx.fill();

    // Glass inset (slightly smaller than frame)
    const GI = 0.08; // inset fraction
    const gbl_x = wbl_x + (wbr_x - wbl_x) * GI + (wtl_x - wbl_x) * GI;
    const gbl_y = wbl_y + (wbr_y - wbl_y) * GI + (wtl_y - wbl_y) * GI;
    const gbr_x = wbr_x - (wbr_x - wbl_x) * GI + (wtr_x - wbr_x) * GI;
    const gbr_y = wbr_y - (wbr_y - wbl_y) * GI + (wtr_y - wbr_y) * GI;
    const gtl_x = wtl_x + (wtr_x - wtl_x) * GI - (wtl_x - wbl_x) * GI;
    const gtl_y = wtl_y + (wtr_y - wtl_y) * GI - (wtl_y - wbl_y) * GI;
    const gtr_x = wtr_x - (wtr_x - wtl_x) * GI - (wtr_x - wbr_x) * GI;
    const gtr_y = wtr_y - (wtr_y - wtl_y) * GI - (wtr_y - wbr_y) * GI;

    // Glass — living exterior view through the window
    // Clip to glass shape for all exterior effects
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(gbl_x, gbl_y);
    ctx.lineTo(gbr_x, gbr_y);
    ctx.lineTo(gtr_x, gtr_y);
    ctx.lineTo(gtl_x, gtl_y);
    ctx.closePath();
    ctx.clip();

    const glassBB = {
      x: Math.min(gbl_x, gtl_x) - 2,
      y: Math.min(gtl_y, gtr_y) - 2,
      w: Math.abs(gbr_x - gbl_x) + 4,
      h: Math.abs(gbl_y - gtl_y) + 4,
    };

    // Time-of-day sky colors
    const isNight = gameHour < 5.5 || gameHour > 20;
    const isDawn = gameHour >= 5.5 && gameHour < 7.5;
    const isDusk = gameHour >= 18 && gameHour <= 20;
    const isDay = gameHour >= 7.5 && gameHour < 18;

    let skyTop: string, skyBot: string;
    if (isNight) {
      skyTop = "#050510"; skyBot = "#0a0a1e";
    } else if (isDawn) {
      const t = (gameHour - 5.5) / 2;
      const r1 = Math.round(5 + t * 50);
      const g1 = Math.round(5 + t * 25);
      const b1 = Math.round(16 + t * 40);
      const r2 = Math.round(10 + t * 120);
      const g2 = Math.round(10 + t * 50);
      const b2 = Math.round(30 + t * 30);
      skyTop = `rgb(${r1},${g1},${b1})`; skyBot = `rgb(${r2},${g2},${b2})`;
    } else if (isDusk) {
      const t = (gameHour - 18) / 2;
      const r1 = Math.round(55 - t * 50);
      const g1 = Math.round(30 - t * 25);
      const b1 = Math.round(56 - t * 40);
      const r2 = Math.round(130 - t * 120);
      const g2 = Math.round(60 - t * 50);
      const b2 = Math.round(60 - t * 30);
      skyTop = `rgb(${r1},${g1},${b1})`; skyBot = `rgb(${r2},${g2},${b2})`;
    } else {
      skyTop = "#1a2848"; skyBot = "#2a3a5a";
    }

    // Sky gradient (top-to-bottom through glass)
    const ghMidX = (gtl_x + gtr_x) / 2;
    const ghMidY = (gtl_y + gtr_y) / 2;
    const ghBotX = (gbl_x + gbr_x) / 2;
    const ghBotY = (gbl_y + gbr_y) / 2;
    const skyGrad = ctx.createLinearGradient(ghMidX, ghMidY, ghBotX, ghBotY);
    skyGrad.addColorStop(0, skyTop);
    skyGrad.addColorStop(0.6, skyBot);
    skyGrad.addColorStop(1, "#0a0a14");
    ctx.fillStyle = skyGrad;
    ctx.fillRect(glassBB.x, glassBB.y, glassBB.w, glassBB.h);

    // Stars (night + dawn/dusk) — stable positions based on wall position hash
    if (!isDay) {
      const starAlpha = isNight ? 0.7 : isDawn ? Math.max(0, 1 - (gameHour - 5.5) / 1.5) * 0.5
                                               : Math.max(0, (gameHour - 18.5) / 1.5) * 0.5;
      if (starAlpha > 0.05) {
        const seed = wall.x * 73 + wall.y * 137;
        const starCount = 6 + (seed % 4);
        const t = timestamp / 1000;
        for (let si = 0; si < starCount; si++) {
          const sx = glassBB.x + ((seed * (si + 1) * 31) % 97) / 97 * glassBB.w;
          const sy = glassBB.y + ((seed * (si + 1) * 47) % 89) / 89 * glassBB.h * 0.7;
          const twinkle = 0.5 + 0.5 * Math.sin(t * (1.5 + si * 0.7) + si * 2.1);
          const r = (0.6 + 0.6 * twinkle) * zoom;
          ctx.fillStyle = `rgba(255,255,230,${(starAlpha * twinkle).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(sx, sy, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Moon (night only, upper-right quadrant of glass)
    if (isNight) {
      const moonX = glassBB.x + glassBB.w * 0.72;
      const moonY = glassBB.y + glassBB.h * 0.22;
      const moonR = 4 * zoom;
      // Moon glow
      const moonGlow = ctx.createRadialGradient(moonX, moonY, moonR * 0.5, moonX, moonY, moonR * 3.5);
      moonGlow.addColorStop(0, "rgba(200,210,240,0.15)");
      moonGlow.addColorStop(1, "rgba(200,210,240,0)");
      ctx.fillStyle = moonGlow;
      ctx.fillRect(moonX - moonR * 4, moonY - moonR * 4, moonR * 8, moonR * 8);
      // Moon disc
      ctx.fillStyle = "#d8dce8";
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
      ctx.fill();
      // Crescent shadow
      ctx.fillStyle = "#0a0a18";
      ctx.beginPath();
      ctx.arc(moonX + moonR * 0.35, moonY - moonR * 0.1, moonR * 0.85, 0, Math.PI * 2);
      ctx.fill();
    }

    // Dawn/dusk horizon glow
    if (isDawn || isDusk) {
      const glowGrad = ctx.createLinearGradient(ghMidX, ghBotY - glassBB.h * 0.3, ghBotX, ghBotY);
      if (isDawn) {
        glowGrad.addColorStop(0, "rgba(255,140,50,0)");
        glowGrad.addColorStop(0.5, "rgba(255,140,50,0.15)");
        glowGrad.addColorStop(1, "rgba(255,100,30,0.25)");
      } else {
        glowGrad.addColorStop(0, "rgba(200,80,40,0)");
        glowGrad.addColorStop(0.5, "rgba(200,80,40,0.12)");
        glowGrad.addColorStop(1, "rgba(180,50,20,0.20)");
      }
      ctx.fillStyle = glowGrad;
      ctx.fillRect(glassBB.x, glassBB.y, glassBB.w, glassBB.h);
    }

    // Glass reflection overlay (subtle, always present — glass is never perfectly clear)
    const reflGrad = ctx.createLinearGradient(ghMidX, ghMidY, ghBotX, ghBotY);
    reflGrad.addColorStop(0, "rgba(180,200,220,0.06)");
    reflGrad.addColorStop(0.5, "rgba(180,200,220,0)");
    reflGrad.addColorStop(1, "rgba(120,140,160,0.04)");
    ctx.fillStyle = reflGrad;
    ctx.fillRect(glassBB.x, glassBB.y, glassBB.w, glassBB.h);

    ctx.restore();

    // Mullion — vertical center bar
    const mcbx = (gbl_x + gbr_x) / 2;
    const mcby = (gbl_y + gbr_y) / 2;
    const mctx = (gtl_x + gtr_x) / 2;
    const mcty = (gtl_y + gtr_y) / 2;
    ctx.strokeStyle = pal.frameHighlight;
    ctx.lineWidth   = 1.2 * zoom;
    ctx.beginPath();
    ctx.moveTo(mcbx, mcby);
    ctx.lineTo(mctx, mcty);
    ctx.stroke();

    // Mullion — horizontal center bar
    const mhl_x = (gbl_x + gtl_x) / 2;
    const mhl_y = (gbl_y + gtl_y) / 2;
    const mhr_x = (gbr_x + gtr_x) / 2;
    const mhr_y = (gbr_y + gtr_y) / 2;
    ctx.beginPath();
    ctx.moveTo(mhl_x, mhl_y);
    ctx.lineTo(mhr_x, mhr_y);
    ctx.stroke();

    // Frame highlight on top and left edges
    ctx.strokeStyle = pal.frameHighlight;
    ctx.lineWidth   = 0.8 * zoom;
    ctx.beginPath();
    ctx.moveTo(wbl_x, wbl_y);
    ctx.lineTo(wtl_x, wtl_y);
    ctx.lineTo(wtr_x, wtr_y);
    ctx.stroke();

    // Sill at the bottom of the window
    ctx.strokeStyle = pal.molding;
    ctx.lineWidth   = 1.5 * zoom;
    ctx.beginPath();
    ctx.moveTo(wbl_x - 2 * zoom, wbl_y + 1 * zoom);
    ctx.lineTo(wbr_x + 2 * zoom, wbr_y + 1 * zoom);
    ctx.stroke();
  }

  // ── 8. Door ───────────────────────────────────────────────────────────────
  if (wall.hasDoor) {
    const DOOR_BOT = 0.0;
    const DOOR_TOP = 0.75;
    const DOOR_L   = 0.25;
    const DOOR_R   = 0.75;

    const [db0x, db0y, db1x, db1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, DOOR_BOT);
    const [dt0x, dt0y, dt1x, dt1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, DOOR_TOP);

    const dbl_x = db0x + (db1x - db0x) * DOOR_L;
    const dbl_y = db0y + (db1y - db0y) * DOOR_L;
    const dbr_x = db0x + (db1x - db0x) * DOOR_R;
    const dbr_y = db0y + (db1y - db0y) * DOOR_R;
    const dtl_x = dt0x + (dt1x - dt0x) * DOOR_L;
    const dtl_y = dt0y + (dt1y - dt0y) * DOOR_L;
    const dtr_x = dt0x + (dt1x - dt0x) * DOOR_R;
    const dtr_y = dt0y + (dt1y - dt0y) * DOOR_R;

    // Door recess — darker void behind the door with depth gradient
    ctx.beginPath();
    ctx.moveTo(dbl_x, dbl_y);
    ctx.lineTo(dbr_x, dbr_y);
    ctx.lineTo(dtr_x, dtr_y);
    ctx.lineTo(dtl_x, dtl_y);
    ctx.closePath();
    const dRecessGrad = ctx.createLinearGradient(dbl_x, dbl_y, dtr_x, dtr_y);
    dRecessGrad.addColorStop(0, "#060610");
    dRecessGrad.addColorStop(0.5, "#0c0c1a");
    dRecessGrad.addColorStop(1, "#080814");
    ctx.fillStyle = dRecessGrad;
    ctx.fill();

    // Inner depth shadow at top of recess
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(dbl_x, dbl_y);
    ctx.lineTo(dbr_x, dbr_y);
    ctx.lineTo(dtr_x, dtr_y);
    ctx.lineTo(dtl_x, dtl_y);
    ctx.closePath();
    ctx.clip();
    const dShadowGrad = ctx.createLinearGradient(
      (dtl_x + dtr_x) / 2, (dtl_y + dtr_y) / 2,
      (dbl_x + dbr_x) / 2, (dbl_y + dbr_y) / 2
    );
    dShadowGrad.addColorStop(0, "rgba(0,0,0,0.5)");
    dShadowGrad.addColorStop(0.25, "rgba(0,0,0,0)");
    ctx.fillStyle = dShadowGrad;
    ctx.fillRect(
      Math.min(dbl_x, dtl_x) - 2, Math.min(dtl_y, dtr_y) - 2,
      Math.abs(dbr_x - dbl_x) + 4, Math.abs(dbl_y - dtl_y) + 4
    );
    ctx.restore();

    // Door panel — slightly inset from door opening, gives visible door surface
    const PANEL_INSET = 0.06;
    const panelL = DOOR_L + PANEL_INSET;
    const panelR = DOOR_R - PANEL_INSET;
    const panelBot = DOOR_BOT + 0.02;
    const panelTop = DOOR_TOP - 0.04;

    const [pb0x, pb0y, pb1x, pb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, panelBot);
    const [pt0x, pt0y, pt1x, pt1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, panelTop);
    const pbl_x = pb0x + (pb1x - pb0x) * panelL;
    const pbl_y = pb0y + (pb1y - pb0y) * panelL;
    const pbr_x = pb0x + (pb1x - pb0x) * panelR;
    const pbr_y = pb0y + (pb1y - pb0y) * panelR;
    const ptl_x = pt0x + (pt1x - pt0x) * panelL;
    const ptl_y = pt0y + (pt1y - pt0y) * panelL;
    const ptr_x = pt0x + (pt1x - pt0x) * panelR;
    const ptr_y = pt0y + (pt1y - pt0y) * panelR;

    // Panel base fill — warm wood-toned door
    ctx.beginPath();
    ctx.moveTo(pbl_x, pbl_y);
    ctx.lineTo(pbr_x, pbr_y);
    ctx.lineTo(ptr_x, ptr_y);
    ctx.lineTo(ptl_x, ptl_y);
    ctx.closePath();
    const doorGrad = ctx.createLinearGradient(pbl_x, pbl_y, ptl_x, ptl_y);
    doorGrad.addColorStop(0, "#2e2218");
    doorGrad.addColorStop(0.5, "#3a2e20");
    doorGrad.addColorStop(1, "#342818");
    ctx.fillStyle = doorGrad;
    ctx.fill();

    // Upper door panel inset (decorative raised panel)
    const upperPanelBot = 0.45;
    const upperPanelTop = 0.68;
    const panelInsetL = panelL + 0.04;
    const panelInsetR = panelR - 0.04;
    const [upb0x, upb0y, upb1x, upb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, upperPanelBot);
    const [upt0x, upt0y, upt1x, upt1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, upperPanelTop);
    const upbl_x = upb0x + (upb1x - upb0x) * panelInsetL;
    const upbl_y = upb0y + (upb1y - upb0y) * panelInsetL;
    const upbr_x = upb0x + (upb1x - upb0x) * panelInsetR;
    const upbr_y = upb0y + (upb1y - upb0y) * panelInsetR;
    const uptl_x = upt0x + (upt1x - upt0x) * panelInsetL;
    const uptl_y = upt0y + (upt1y - upt0y) * panelInsetL;
    const uptr_x = upt0x + (upt1x - upt0x) * panelInsetR;
    const uptr_y = upt0y + (upt1y - upt0y) * panelInsetR;

    ctx.strokeStyle = "#221a10";
    ctx.lineWidth = 0.8 * zoom;
    ctx.beginPath();
    ctx.moveTo(upbl_x, upbl_y);
    ctx.lineTo(upbr_x, upbr_y);
    ctx.lineTo(uptr_x, uptr_y);
    ctx.lineTo(uptl_x, uptl_y);
    ctx.closePath();
    ctx.stroke();
    // Inner highlight on panel top edge
    ctx.strokeStyle = "rgba(255,240,200,0.08)";
    ctx.lineWidth = 0.5 * zoom;
    ctx.beginPath();
    ctx.moveTo(uptl_x, uptl_y);
    ctx.lineTo(uptr_x, uptr_y);
    ctx.stroke();

    // Lower door panel inset
    const lowerPanelBot = 0.08;
    const lowerPanelTop = 0.38;
    const [lpb0x, lpb0y, lpb1x, lpb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, lowerPanelBot);
    const [lpt0x, lpt0y, lpt1x, lpt1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, lowerPanelTop);
    const lpbl_x = lpb0x + (lpb1x - lpb0x) * panelInsetL;
    const lpbl_y = lpb0y + (lpb1y - lpb0y) * panelInsetL;
    const lpbr_x = lpb0x + (lpb1x - lpb0x) * panelInsetR;
    const lpbr_y = lpb0y + (lpb1y - lpb0y) * panelInsetR;
    const lptl_x = lpt0x + (lpt1x - lpt0x) * panelInsetL;
    const lptl_y = lpt0y + (lpt1y - lpt0y) * panelInsetL;
    const lptr_x = lpt0x + (lpt1x - lpt0x) * panelInsetR;
    const lptr_y = lpt0y + (lpt1y - lpt0y) * panelInsetR;

    ctx.strokeStyle = "#221a10";
    ctx.lineWidth = 0.8 * zoom;
    ctx.beginPath();
    ctx.moveTo(lpbl_x, lpbl_y);
    ctx.lineTo(lpbr_x, lpbr_y);
    ctx.lineTo(lptr_x, lptr_y);
    ctx.lineTo(lptl_x, lptl_y);
    ctx.closePath();
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,240,200,0.08)";
    ctx.lineWidth = 0.5 * zoom;
    ctx.beginPath();
    ctx.moveTo(lptl_x, lptl_y);
    ctx.lineTo(lptr_x, lptr_y);
    ctx.stroke();

    // Door handle — small knob on the right side
    const handleT = 0.38;
    const handleF = panelR - 0.06;
    const [hb0x, hb0y, hb1x, hb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, handleT);
    const handleX = hb0x + (hb1x - hb0x) * handleF;
    const handleY = hb0y + (hb1y - hb0y) * handleF;
    const handleR = 1.4 * zoom;
    // Handle plate
    ctx.fillStyle = "#8a7a50";
    ctx.beginPath();
    ctx.ellipse(handleX, handleY, handleR * 1.6, handleR * 2.2, 0, 0, Math.PI * 2);
    ctx.fill();
    // Handle knob
    ctx.fillStyle = "#c4aa60";
    ctx.beginPath();
    ctx.arc(handleX, handleY, handleR, 0, Math.PI * 2);
    ctx.fill();
    // Handle highlight
    ctx.fillStyle = "rgba(255,240,180,0.35)";
    ctx.beginPath();
    ctx.arc(handleX - handleR * 0.3, handleY - handleR * 0.3, handleR * 0.4, 0, Math.PI * 2);
    ctx.fill();

    // Hinges — two small rectangles on the left side
    for (const hingeT of [0.18, 0.58]) {
      const hingeF = panelL + 0.02;
      const [hhb0x, hhb0y, hhb1x, hhb1y] = wallLerp(bx0, by0, bx1, by1, tx0, ty0, tx1, ty1, hingeT);
      const hhx = hhb0x + (hhb1x - hhb0x) * hingeF;
      const hhy = hhb0y + (hhb1y - hhb0y) * hingeF;
      ctx.fillStyle = "#5a4a30";
      ctx.fillRect(hhx - 1 * zoom, hhy - 2 * zoom, 2 * zoom, 4 * zoom);
      ctx.fillStyle = "rgba(255,230,160,0.12)";
      ctx.fillRect(hhx - 0.5 * zoom, hhy - 2 * zoom, 1 * zoom, 0.8 * zoom);
    }

    // Door frame
    ctx.strokeStyle = pal.frame;
    ctx.lineWidth   = 2.0 * zoom;
    ctx.beginPath();
    ctx.moveTo(dbl_x, dbl_y);
    ctx.lineTo(dtl_x, dtl_y);
    ctx.lineTo(dtr_x, dtr_y);
    ctx.lineTo(dbr_x, dbr_y);
    ctx.stroke();

    // Frame highlight on top
    ctx.strokeStyle = pal.frameHighlight;
    ctx.lineWidth   = 0.8 * zoom;
    ctx.beginPath();
    ctx.moveTo(dtl_x, dtl_y);
    ctx.lineTo(dtr_x, dtr_y);
    ctx.stroke();

    // Frame inner shadow — subtle depth on left/right edges
    ctx.strokeStyle = "rgba(0,0,0,0.2)";
    ctx.lineWidth = 0.6 * zoom;
    ctx.beginPath();
    ctx.moveTo(dbl_x + 1 * zoom, dbl_y);
    ctx.lineTo(dtl_x + 1 * zoom, dtl_y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(dbr_x - 1 * zoom, dbr_y);
    ctx.lineTo(dtr_x - 1 * zoom, dtr_y);
    ctx.stroke();

    // Threshold — warm amber line at floor level
    ctx.strokeStyle = "#6a5a3a";
    ctx.lineWidth   = 1.5 * zoom;
    ctx.beginPath();
    ctx.moveTo(dbl_x, dbl_y);
    ctx.lineTo(dbr_x, dbr_y);
    ctx.stroke();
    // Threshold highlight
    ctx.strokeStyle = "rgba(255,220,140,0.1)";
    ctx.lineWidth = 0.6 * zoom;
    ctx.beginPath();
    ctx.moveTo(dbl_x + 1, dbl_y - 0.5);
    ctx.lineTo(dbr_x - 1, dbr_y - 0.5);
    ctx.stroke();
  }

  // ── Outer edge lines ────────────────────────────────────────────────────
  ctx.strokeStyle = pal.accent;
  ctx.lineWidth   = 0.5;
  ctx.beginPath();
  ctx.moveTo(bx0, by0);
  ctx.lineTo(tx0, ty0);
  ctx.lineTo(tx1, ty1);
  ctx.lineTo(bx1, by1);
  ctx.closePath();
  ctx.stroke();

  ctx.restore();
}

// ─── Floor Rendering ──────────────────────────────────────────────────────────

/**
 * Render the entire floor grid to the canvas.
 *
 * Tiles are drawn back-to-front (top-left to bottom-right in tile space),
 * which is the correct isometric order for flat floors. Elevated tiles
 * are shifted upward on screen by their elevation * Z_SCALE.
 *
 * Void tiles are skipped entirely — the canvas background shows through.
 * This lets rooms have non-rectangular shapes, alcoves, balconies.
 *
 * @param ctx     Canvas 2D context
 * @param grid    The tile grid to render
 * @param camera  Current camera state
 */
export function renderFloor(
  ctx: CanvasRenderingContext2D,
  grid: TileGrid,
  camera: Camera,
  timestamp: number = 0
): void {
  const zoom = camera.zoom;
  const tileW = TILE_W * zoom;
  const tileH = TILE_H * zoom;

  // Isometric back-to-front: iterate y outer, x inner.
  // This ensures tiles that are "in front" (higher y) get drawn after
  // tiles that are "behind" (lower y), even on a flat floor.
  for (let ty = 0; ty < grid.height; ty++) {
    for (let tx = 0; tx < grid.width; tx++) {
      const tile = grid.getTile(tx, ty);

      // Void tiles are the absence of floor — skip them
      if (tile.type === "void") continue;

      const palette = TILE_PALETTE[tile.type];

      // Project tile top-left corner to screen.
      // We use tx, ty directly (not tile center) because drawTileDiamond
      // expects the top point of the diamond, which is the tile's north corner.
      const raw = worldToScreen(tx, ty, tile.elevation, true);
      const screen = applyCamera(raw, camera);

      drawTileShaded(ctx, screen.x, screen.y, palette, tileW, tileH, tile.type, tx, ty, tile.variant, timestamp);

      // ── Elevation risers ──────────────────────────────────────────────
      // If this tile is higher than its south or east neighbor, draw the
      // visible vertical face so the tile doesn't float.
      const elev = tile.elevation;
      if (elev <= 0) continue;

      const halfW = tileW / 2;
      const halfH = tileH / 2;

      // South neighbor (tile at ty+1) — draws the east-facing riser
      const southTile = grid.getTile(tx, ty + 1);
      const southDiff = elev - southTile.elevation;
      if (southDiff > 0) {
        const sZPx = southDiff * Z_SCALE * zoom;
        // The south edge of this tile diamond: bottom → left
        const bx = screen.x;
        const by = screen.y + tileH;
        const lx = screen.x - halfW;
        const ly = screen.y + halfH;

        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(bx, by);
        ctx.lineTo(bx, by + sZPx);
        ctx.lineTo(lx, ly + sZPx);
        ctx.closePath();

        // South riser — lighter than east (catches more ambient)
        const sGrad = ctx.createLinearGradient(lx, ly, lx, ly + sZPx);
        sGrad.addColorStop(0, palette.base);
        sGrad.addColorStop(0.3, palette.dark);
        sGrad.addColorStop(1, "#0a0a10");
        ctx.fillStyle = sGrad;
        ctx.fill();
        ctx.strokeStyle = palette.dark;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      // East neighbor (tile at tx+1) — draws the west-facing riser
      const eastTile = grid.getTile(tx + 1, ty);
      const eastDiff = elev - eastTile.elevation;
      if (eastDiff > 0) {
        const eZPx = eastDiff * Z_SCALE * zoom;
        const rx = screen.x + halfW;
        const ry = screen.y + halfH;
        const bx = screen.x;
        const by = screen.y + tileH;

        ctx.beginPath();
        ctx.moveTo(rx, ry);
        ctx.lineTo(bx, by);
        ctx.lineTo(bx, by + eZPx);
        ctx.lineTo(rx, ry + eZPx);
        ctx.closePath();

        // East riser — darker (less light from this angle)
        const eGrad = ctx.createLinearGradient(rx, ry, rx, ry + eZPx);
        eGrad.addColorStop(0, palette.dark);
        eGrad.addColorStop(0.4, "#141418");
        eGrad.addColorStop(1, "#080810");
        ctx.fillStyle = eGrad;
        ctx.fill();
        ctx.strokeStyle = palette.dark;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }
  }
}

/**
 * Render wall segments. Call after renderFloor but before drawing characters
 * and furniture that should appear in front of walls.
 *
 * @param ctx    Canvas 2D context
 * @param walls  Wall segments for the room
 * @param camera Current camera state
 */
export function renderWalls(
  ctx: CanvasRenderingContext2D,
  walls: WallSegment[],
  camera: Camera,
  gameHour = 12,
  timestamp = 0,
): void {
  // Walls should be sorted by depth too — further walls drawn first.
  // Simple sort: walls with lower (wx + wy) are further back.
  const sorted = [...walls].sort((a, b) => (a.x + a.y) - (b.x + b.y));

  for (const wall of sorted) {
    drawWallSegment(ctx, wall, camera, gameHour, timestamp);
  }
}

// ─── Hover Highlight ──────────────────────────────────────────────────────────

/**
 * Draw a hover highlight on a specific tile — a gentle warm glow to show
 * the player where they're about to walk or place furniture.
 *
 * @param ctx    Canvas 2D context
 * @param tx     Tile X to highlight
 * @param ty     Tile Y to highlight
 * @param grid   The grid (to get elevation)
 * @param camera Current camera
 * @param color  Highlight color (default warm amber)
 */
export function highlightTile(
  ctx: CanvasRenderingContext2D,
  tx: number,
  ty: number,
  grid: TileGrid,
  camera: Camera,
  color: string = "rgba(255, 200, 100, 0.25)"
): void {
  const tile = grid.getTile(tx, ty);
  if (tile.type === "void") return;

  const zoom = camera.zoom;
  const tileW = TILE_W * zoom;
  const tileH = TILE_H * zoom;

  const raw = worldToScreen(tx, ty, tile.elevation, true);
  const screen = applyCamera(raw, camera);

  ctx.save();
  ctx.globalCompositeOperation = "screen";
  drawTileDiamond(ctx, screen.x, screen.y, tileW, tileH, color);
  ctx.restore();
}
